import { useIsMutating, useMutation } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { Macros } from 'interfaces/Macros';
import { Coin } from 'interfaces/MeshMaven';
import { useDispatch } from 'react-redux';
import { FeedbackAnalysisAPIResponse } from 'screens/common/MLCommentTextGenerationCompV1/types/FeedbackAnalysis';
import { RephraseTextAPIResponse } from 'screens/common/MLCommentTextGenerationCompV1/types/RephraseText';
import { RephraseOption } from 'screens/common/RephraseOptions/types';
import { usePrimaryPanelAction } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/slice/feedbackSlice';
import { useIsMutationCancelled } from 'utils/helpers/reactQueryHelpers';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { updateAddViolationDrawer, useSelectAddViolationDrawerData } from '../slice/addViolation.slice';

const getUpdatedRevisionsForAnalysis = (
  revisions: any[],
  revisionNumber: number,
  analysis: Coin,
  isReanalyzeEnabled: boolean
) =>
  revisions.map((revision, index) => {
    if (index === revisionNumber) {
      return {
        ...revision,
        feedbackAnalysis: analysis,
        isReanalyzeEnabled,
      };
    }
    return revision;
  });

const getViolationAnalysis = ({
  groupId,
  revisionNumber,
  text,
  rephraseInstruction,
}: {
  groupId: string;
  revisionNumber: number;
  text: string;
  rephraseInstruction?: RephraseOption;
}): Promise<FeedbackAnalysisAPIResponse> =>
  sendRequest({
    url: endpoints.disciplinaryActions.analyze,
    method: 'POST',
    credentials: 'include',
    body: {
      serviceName: 'da_analyze',
      groupId,
      revisionNumber,
      text,
      rephraseInstruction,
    },
    queryParams: {},
  });

export const useCreateViolationAnalysisMutation = () => {
  const dispatch = useDispatch();

  const { meshMavenGroupId } = useSelectAddViolationDrawerData();
  const isMutationCancelled = useIsMutationCancelled();

  return useMutation<
    FeedbackAnalysisAPIResponse,
    { message?: string; status?: number },
    { revisionNumber: number; description: string; groupId: string; revisions: any[] }
  >(
    ({ revisionNumber, description, groupId }) =>
      getViolationAnalysis({
        groupId: groupId || meshMavenGroupId,
        revisionNumber: revisionNumber + 1,
        text: description,
      }),
    {
      mutationKey: [REACT_QUERY_KEYS.disciplinaryActions.analyzeText],
      onSuccess: (data, { revisions, revisionNumber }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.disciplinaryActions.analyzeText])) {
          return;
        }
        if (data.error) {
          dispatch(updateAddViolationDrawer({ analysisError: data.message }));
        } else if (data.entity) {
          dispatch(updateAddViolationDrawer({ analysisError: null }));
          dispatch(
            updateAddViolationDrawer({
              revisions: getUpdatedRevisionsForAnalysis(revisions, revisionNumber, data.entity.labels, false),
            })
          );
        } else {
          dispatch(updateAddViolationDrawer({ analysisError: 'Uh-oh! Looks like an unknown error happened.' }));
        }
      },
      onError: (error, { revisions, revisionNumber }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.disciplinaryActions.analyzeText])) {
          return;
        }
        if (error.status === 500 || !error.status) {
          dispatch(
            updateAddViolationDrawer({
              revisions: getUpdatedRevisionsForAnalysis(revisions, revisionNumber, null, true),
            })
          );
        }
        dispatch(
          updateAddViolationDrawer({ analysisError: error?.message ?? 'Uh-oh! Looks like an unknown error happened.' })
        );
      },
    }
  );
};

export const useFeedbackAnalysisLoading = () =>
  useIsMutating({
    mutationKey: [REACT_QUERY_KEYS.disciplinaryActions.analyzeText],
  }) > 0;

const rephraseText = ({
  groupId,
  revisionNumber,
  text,
  rephraseInstruction,
}: {
  groupId: string;
  revisionNumber: number;
  text: string;
  rephraseInstruction: RephraseOption;
}): Promise<RephraseTextAPIResponse> =>
  sendRequest({
    url: endpoints.disciplinaryActions.rephrase,
    method: 'POST',
    credentials: 'include',
    body: {
      serviceName: 'da_rephrase',
      groupId,
      revisionNumber,
      text,
      rephraseInstruction,
    },
    queryParams: {},
  });

export const useRephraseTextMutation = () => {
  const dispatch = useDispatch();

  const action = usePrimaryPanelAction();
  const drawerData = useSelectAddViolationDrawerData();
  const { revisionNumber, revisions, selectedRevision, meshMavenGroupId } = drawerData;

  const { mutate: analyzeFeedback } = useCreateViolationAnalysisMutation();

  const isMutationCancelled = useIsMutationCancelled();

  return useMutation<
    { data: RephraseTextAPIResponse; macros: Macros },
    { message?: string },
    {
      rephraseInstruction: RephraseOption;
    }
  >(
    ({ rephraseInstruction }) => {
      const editorMetadata: { text: string } =
        typeof selectedRevision.updatedState === 'object'
          ? { text: selectedRevision.updatedState.getText() }
          : { text: selectedRevision.updatedState };

      const text = editorMetadata.text;

      return rephraseText({
        groupId: meshMavenGroupId,
        revisionNumber: revisionNumber + 1,
        text,
        rephraseInstruction,
      }).then((data) => ({ data }));
    },
    {
      mutationKey: [REACT_QUERY_KEYS.disciplinaryActions.RephraseText],
      onSuccess: ({ data, macros }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.disciplinaryActions.RephraseText])) {
          return;
        }
        if (data.error) {
          dispatch(updateAddViolationDrawer({ rephraseError: data.message }));
        } else if (data.entity) {
          const newRevision = {
            requestId: data.entity.requestId,
            originalState: data?.entity?.rephrasedText,
            updatedState: data?.entity?.rephrasedText,
            isAIFeedbackProvided: false,
            isReanalyzeEnabled: false,
            feedbackAnalysis: { context: 0, observations: 0, impact: 0, next_steps: 0 } as Coin,
          };

          const newRevisions = [];
          revisions.forEach((revision, index) => {
            if (index === revisionNumber) {
              newRevisions.push({ ...revision, isReanalyzeEnabled: false });
            } else {
              newRevisions.push(revision);
            }
          });

          newRevisions.push(newRevision);

          dispatch(
            updateAddViolationDrawer({
              revisionNumber: newRevisions.length - 1,
              rephrasedDescription: data?.entity?.rephrasedText || '',
              rephrasedDescriptionHtml: `<p>${data?.entity?.rephrasedText || ''}</p>`,
              revisions: newRevisions,
              rephraseError: null,
            })
          );

          analyzeFeedback({
            revisionNumber: newRevisions.length - 1,
            description: data?.entity?.rephrasedText,
            groupId: meshMavenGroupId,
          });
        } else {
          dispatch(updateAddViolationDrawer({ rephraseError: 'Uh-oh! Looks like an unknown error happened.' }));
        }
      },
      onError: (error) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.disciplinaryActions.RephraseText])) {
          return;
        }
        dispatch(
          updateAddViolationDrawer({ rephraseError: error?.message ?? 'Uh-oh! Looks like an unknown error happened.' })
        );
      },
    }
  );
};

export const useRephraseTextLoading = () =>
  useIsMutating({
    mutationKey: [REACT_QUERY_KEYS.disciplinaryActions.RephraseText],
  }) > 0;
